import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Restore session - always use localStorage for persistence
  useEffect(() => {
    const restoreSession = async () => {
      const savedToken = localStorage.getItem('deal-token');
      if (savedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          const res = await axios.get(`${API}/auth/me`);
          setUser(res.data);
          setToken(savedToken);
        } catch (err) {
          console.warn('Session restore failed:', err?.message);
          localStorage.removeItem('deal-token');
        }
      }
      setLoading(false);
    };
    restoreSession();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('deal-token', newToken);
    toast.success(`Bienvenido, ${userData.name}`);
    return userData;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token: newToken, user: userData } = res.data;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('deal-token', newToken);
    toast.success(`Cuenta creada. Bienvenido, ${userData.name}`);
    return userData;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('deal-token');
    delete axios.defaults.headers.common['Authorization'];
    toast.info('Sesion cerrada');
  }, []);

  const updateProfile = useCallback(async (data) => {
    const res = await axios.put(`${API}/auth/profile`, data);
    setUser(res.data);
    return res.data;
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const hasPermission = (perm) => {
    if (isAdmin) return true;
    return user?.permissions?.[perm] === true;
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, logout, updateProfile,
      isAuthenticated, isAdmin, isManager,
      hasPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};
